import { Controller } from "@hotwired/stimulus"

// The New figure page's pictures (figures/_picture_tray): dropped, pasted or
// chosen pictures wait here and go with the form when the figure is
// created. Files ride in the form's `pictures[]` input; `picture_slots[]`
// says their order among pictures dropped from other sites, which are sent
// as addresses ("file" for the next file, else the address).
export default class extends Controller {
  static targets = ["zone", "files", "slots", "list"]

  connect() {
    this.items = []
    this.paste = (e) => this.pasted(e)
    document.addEventListener("paste", this.paste)
  }

  disconnect() {
    document.removeEventListener("paste", this.paste)
    this.items.forEach((item) => item.file && URL.revokeObjectURL(item.src))
  }

  choose() {
    this.filesTarget.click()
  }

  over(e) {
    e.preventDefault()
    this.zoneTarget.classList.add("is-over")
  }

  leave() {
    this.zoneTarget.classList.remove("is-over")
  }

  drop(e) {
    e.preventDefault()
    this.leave()
    const files = [...e.dataTransfer.files].filter((f) => f.type.startsWith("image/"))
    if (files.length) return this.add(files.map((file) => ({ file })))
    const url = this.droppedUrl(e.dataTransfer)
    if (url) this.add([ { url } ])
    else alert("That doesn't look like a picture.")
  }

  // Chosen from the file picker: the input's own list is rebuilt from the
  // tray, so take them out of it first.
  picked() {
    const files = [...this.filesTarget.files]
    this.add(files.map((file) => ({ file })))
  }

  // A picture pasted anywhere on the page, unless it's going into a field.
  pasted(e) {
    const item = [...(e.clipboardData?.items || [])].find((i) => i.type.startsWith("image/"))
    if (!item) return
    const file = item.getAsFile()
    if (!file) return
    e.preventDefault()
    this.add([ { file: new File([ file ], file.name || "pasted.png", { type: file.type }) } ])
  }

  remove(e) {
    const index = Number(e.currentTarget.dataset.index)
    const [ item ] = this.items.splice(index, 1)
    if (item?.file) URL.revokeObjectURL(item.src)
    this.sync()
  }

  add(items) {
    items.forEach((item) => this.items.push({ ...item, src: item.file ? URL.createObjectURL(item.file) : item.url }))
    this.sync()
  }

  // Puts the tray into the form (files, then their order) and redraws it.
  sync() {
    const transfer = new DataTransfer()
    this.items.filter((i) => i.file).forEach((i) => transfer.items.add(i.file))
    this.filesTarget.files = transfer.files

    this.slotsTarget.replaceChildren(...this.items.map((i) => {
      const input = document.createElement("input")
      Object.assign(input, { type: "hidden", name: "picture_slots[]", value: i.file ? "file" : i.url })
      return input
    }))

    this.listTarget.querySelectorAll(".fe-tray-item:not(.is-fixed)").forEach((li) => li.remove())
    this.items.forEach((item, index) => {
      const li = document.createElement("li")
      li.className = "fe-tray-item"
      const img = Object.assign(document.createElement("img"), { src: item.src, alt: "" })
      const button = Object.assign(document.createElement("button"), { type: "button", title: "Take it out", innerHTML: "&times;" })
      button.className = "fe-tray-remove"
      button.dataset.index = index
      button.dataset.action = "picture-tray#remove"
      li.append(img, button)
      this.listTarget.append(li)
    })
  }

  // An image dragged from another page: its address.
  droppedUrl(data) {
    const uri = data.getData("text/uri-list")
    if (uri && /\.(jpe?g|png|gif|webp)(\?|$)/i.test(uri)) return uri
    const html = data.getData("text/html")
    const src = html && new DOMParser().parseFromString(html, "text/html").querySelector("img")?.getAttribute("src")
    return src && /^https?:/.test(src) ? src : null
  }
}
